import React from 'react'
import { AlertCircle, AlertTriangle, Info, X } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'

export type ErrorBannerTone = 'error' | 'warning' | 'info'

export interface ErrorBannerProps {
  /** Bos/null ise bilesen hic render edilmez. */
  message: string | null | undefined
  title?: string | undefined
  tone?: ErrorBannerTone | undefined
  onDismiss?: (() => void) | undefined
  onRetry?: (() => void) | undefined
  className?: string | undefined
}

const TONE_STYLES: Record<ErrorBannerTone, { box: string; icon: string; title: string; body: string }> = {
  error: {
    box: 'bg-red-50 border-red-200',
    icon: 'text-red-600',
    title: 'text-red-800',
    body: 'text-red-700',
  },
  warning: {
    box: 'bg-yellow-50 border-yellow-200',
    icon: 'text-yellow-600',
    title: 'text-yellow-800',
    body: 'text-yellow-700',
  },
  info: {
    box: 'bg-blue-50 border-blue-200',
    icon: 'text-blue-600',
    title: 'text-blue-800',
    body: 'text-blue-700',
  },
}

const ToneIcon: React.FC<{ tone: ErrorBannerTone; className: string }> = ({ tone, className }) => {
  if (tone === 'warning') return <AlertTriangle className={className} />
  if (tone === 'info') return <Info className={className} />
  return <AlertCircle className={className} />
}

/**
 * Baglam ici hata kutusu. Projede ayni gorevi goren 11 kopyala-yapistir varyant
 * (5 farkli gorsel) vardi; hepsi bunun yerine kullanilir.
 *
 * Modal icinde kullanildiginda hata kullanicinin baktigi yuzeyde kalir --
 * sayfa govdesindeki banner modal backdrop'unun altinda gorunmez olurdu.
 */
const ErrorBanner: React.FC<ErrorBannerProps> = ({
  message,
  title,
  tone = 'error',
  onDismiss,
  onRetry,
  className = '',
}) => {
  const { t } = useLanguage()

  if (!message) return null

  const styles = TONE_STYLES[tone]
  const heading = title ?? t.errors.title

  return (
    <div
      role="alert"
      aria-live="assertive"
      className={`rounded-lg border ${styles.box} p-4 ${className}`}
    >
      <div className="flex items-start gap-3">
        <ToneIcon tone={tone} className={`w-5 h-5 flex-shrink-0 mt-0.5 ${styles.icon}`} />

        <div className="flex-1 min-w-0">
          <h3 className={`text-sm font-medium ${styles.title}`}>{heading}</h3>
          <p className={`text-sm mt-1 break-words ${styles.body}`}>{message}</p>

          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className={`mt-2 text-sm font-medium underline underline-offset-2 ${styles.title} hover:opacity-80`}
            >
              {t.errors.retry}
            </button>
          )}
        </div>

        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label={t.errors.dismiss}
            className={`flex-shrink-0 ${styles.icon} hover:opacity-70 transition-opacity`}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  )
}

export default ErrorBanner
